import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import '../styles/Analytics.css';

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30'); // days
  const [popularProducts, setPopularProducts] = useState([]);
  const [slowMovingProducts, setSlowMovingProducts] = useState([]);
  const [salesTrend, setSalesTrend] = useState([]);
  const [paymentDistribution, setPaymentDistribution] = useState([]);
  const [summary, setSummary] = useState({
    totalSales: 0,
    totalOrders: 0,
    averageOrderValue: 0,
    topProduct: ''
  });

  const COLORS = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b', '#fa709a'];

  useEffect(() => {
    fetchAnalyticsData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      const days = parseInt(dateRange);
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      startDate.setHours(0, 0, 0, 0);

      const salesQuery = query(
        collection(db, 'sales'),
        where('date', '>=', startDate.toISOString())
      );

      const salesSnapshot = await getDocs(salesQuery);
      const salesData = salesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Process data
      processPopularProducts(salesData);
      processSlowMovingProducts(salesData);
      processSalesTrend(salesData, days);
      processPaymentDistribution(salesData);
      calculateSummary(salesData);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setLoading(false);
    }
  };

  const processPopularProducts = (salesData) => {
    const productMap = {};

    salesData.forEach(sale => {
      sale.items.forEach(item => {
        if (productMap[item.name]) {
          productMap[item.name].quantity += item.quantity;
          productMap[item.name].revenue += item.price * item.quantity;
        } else {
          productMap[item.name] = {
            name: item.name,
            quantity: item.quantity,
            revenue: item.price * item.quantity
          };
        }
      });
    });

    const products = Object.values(productMap)
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 10);

    setPopularProducts(products);
  };

  const processSlowMovingProducts = (salesData) => {
    const productMap = {};

    salesData.forEach(sale => {
      sale.items.forEach(item => {
        if (productMap[item.name]) {
          productMap[item.name].quantity += item.quantity;
        } else {
          productMap[item.name] = {
            name: item.name,
            quantity: item.quantity
          };
        }
      });
    });

    const products = Object.values(productMap)
      .sort((a, b) => a.quantity - b.quantity)
      .slice(0, 10);

    setSlowMovingProducts(products);
  };

  const processSalesTrend = (salesData, days) => {
    const trendMap = {};

    // Initialize all days
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateKey = date.toISOString().split('T')[0];
      trendMap[dateKey] = {
        date: dateKey,
        sales: 0,
        orders: 0
      };
    }

    // Fill with actual data
    salesData.forEach(sale => {
      const dateKey = sale.date.split('T')[0];
      if (trendMap[dateKey]) {
        trendMap[dateKey].sales += sale.totalWithGST;
        trendMap[dateKey].orders += 1;
      }
    });

    const trend = Object.values(trendMap).map(item => ({
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      sales: Math.round(item.sales),
      orders: item.orders
    }));

    setSalesTrend(trend);
  };

  const processPaymentDistribution = (salesData) => {
    const paymentMap = {
      cash: 0,
      transfer: 0,
      card: 0
    };

    salesData.forEach(sale => {
      const type = sale.paymentType || 'cash';
      paymentMap[type] += sale.totalWithGST;
    });

    const distribution = [
      { name: 'Cash', value: Math.round(paymentMap.cash) },
      { name: 'Transfer', value: Math.round(paymentMap.transfer) },
      { name: 'Card', value: Math.round(paymentMap.card) }
    ].filter(item => item.value > 0);

    setPaymentDistribution(distribution);
  };

  const calculateSummary = (salesData) => {
    const totalSales = salesData.reduce((sum, sale) => sum + sale.totalWithGST, 0);
    const totalOrders = salesData.length;
    const averageOrderValue = totalOrders > 0 ? totalSales / totalOrders : 0;

    // Find top product
    const productMap = {};
    salesData.forEach(sale => {
      sale.items.forEach(item => {
        productMap[item.name] = (productMap[item.name] || 0) + item.quantity;
      });
    });

    const topProduct = Object.entries(productMap)
      .sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';

    setSummary({
      totalSales,
      totalOrders,
      averageOrderValue,
      topProduct
    });
  };

  if (loading) {
    return <div className="loading">Loading analytics...</div>;
  }

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h1>📊 Analytics Dashboard</h1>
        <div className="date-range-selector">
          <label>Time Period:</label>
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="60">Last 60 Days</option>
            <option value="90">Last 90 Days</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="analytics-summary">
        <div className="summary-card">
          <div className="summary-icon">💰</div>
          <div className="summary-content">
            <h3>Total Sales</h3>
            <p className="summary-value">MVR {summary.totalSales.toFixed(2)}</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">📦</div>
          <div className="summary-content">
            <h3>Total Orders</h3>
            <p className="summary-value">{summary.totalOrders}</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">📈</div>
          <div className="summary-content">
            <h3>Avg Order Value</h3>
            <p className="summary-value">MVR {summary.averageOrderValue.toFixed(2)}</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">⭐</div>
          <div className="summary-content">
            <h3>Top Product</h3>
            <p className="summary-value">{summary.topProduct}</p>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="charts-grid">
        {/* Sales Trend Chart */}
        <div className="chart-card full-width">
          <h2>📈 Sales Trend</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={salesTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="sales"
                stroke="#667eea"
                strokeWidth={3}
                name="Sales (MVR)"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="orders"
                stroke="#43e97b"
                strokeWidth={3}
                name="Orders"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Popular Products Chart */}
        <div className="chart-card">
          <h2>🔥 Popular Products (Top 10)</h2>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={popularProducts} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={150} />
              <Tooltip />
              <Legend />
              <Bar dataKey="quantity" fill="#667eea" name="Quantity Sold" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Payment Distribution Chart */}
        <div className="chart-card">
          <h2>💳 Payment Distribution</h2>
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={paymentDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {paymentDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Slow Moving Products Chart */}
        <div className="chart-card full-width">
          <h2>🐌 Slow Moving Products (Bottom 10)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={slowMovingProducts}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="quantity" fill="#fa709a" name="Quantity Sold" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
